import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import {
  IliasConnection,
  IliasContentScope,
  IliasOverviewService,
  IliasPluginState,
  IliasSyncSettings,
} from '../../../services/ilias-overview.service';
import {
  IliasSoapConfig,
  IliasSoapInput,
  LmsRegistryService,
} from '../../../services/lms-registry.service';

/**
 * ILIAS-Verbindung einrichten — das Gegenstück zum Moodle-Verbindungsdialog.
 *
 * Bewusst dieselbe Bauform: Kopfzeile, Zustandszeile, je Verbindung eine
 * Kachel mit Zustandsmarken und Einstellungsgruppen, Fusszeile. Das gemeinsame
 * Aussehen steht in `_lms-connect-dialog.scss`.
 *
 * Abgegrenzt von `/ilias`: dort steht, **was es in ILIAS gibt** (Kurse,
 * Lernende, Dozenten). Hier wird die Verbindung eingerichtet — so wie die
 * Moodle-Kachel den Zugang regelt und `/moodle` die Daten zeigt.
 */
@Component({
  selector: 'app-ilias-connect-dialog',
  templateUrl: './ilias-connect-dialog.component.html',
  styleUrl: './ilias-connect-dialog.component.scss',
})
export class IliasConnectDialogComponent implements OnInit, OnDestroy {
  loading = true;
  connections: IliasConnection[] = [];

  /** Zugang je Verbindung — der Dialog zeigt alle nebeneinander, wie Moodle
   *  seine Instanzen. */
  soap: Record<string, IliasSoapConfig> = {};
  forms: Record<string, {
    soap_url: string;
    ilias_client: string;
    username: string;
    password: string;
    template_ref_id: number | null;
  }> = {};

  busy: Record<string, boolean> = {};
  testing: Record<string, boolean> = {};
  syncing: Record<string, boolean> = {};
  guideOpen = false;

  /** Welcher der beiden Wege gerade gezeigt wird.
   *
   *  'plugin' — das Plugin laeuft in ILIAS, keine Zugangsdaten noetig.
   *  'legacy' — LTI + SOAP, fuer Haeuser, die kein Plugin einspielen duerfen.
   *
   *  Vorher standen beide untereinander, und wer sich entschieden hatte,
   *  las trotzdem die Teile des anderen. */
  pathTab: 'plugin' | 'legacy' = 'plugin';

  setPathTab(tab: 'plugin' | 'legacy'): void {
    this.pathTab = tab;
    // Die Schritte immer aufgeklappt zeigen, wenn jemand den alten Weg
    // ausdruecklich waehlt — er hat die Frage „kein Plugin moeglich?" damit
    // schon beantwortet.
    if (tab === 'legacy') this.guideOpen = true;
  }

  /** Beim Oeffnen steht immer der Plugin-Weg.
   *
   *  Auch wenn eine Verbindung noch am alten Weg haengt: das ist der Weg,
   *  den wir empfehlen, und wer den anderen braucht, ist einen Klick
   *  entfernt. Vorher entschied ein hinterlegter SOAP-Zugang — ein Kunde
   *  mit Altbestand landete damit jedes Mal wieder in den zehn Schritten,
   *  statt den kurzen Weg ueberhaupt zu sehen. */
  private pickInitialPath(): void {
    this.pathTab = 'plugin';
  }

  /** Wo das Paket, die Anleitung und die Aenderungen liegen.
   *
   *  Feste Adressen und keine i18n-Werte: eine URL ist keine Uebersetzung,
   *  und zwei Sprachstaende, die auseinanderlaufen, waeren zwei kaputte
   *  Links. Das Repo ist oeffentlich, die Releases sind ohne Anmeldung
   *  erreichbar — genau deshalb steht hier ueberhaupt ein Link. */
  readonly pluginDownloadUrl =
    'https://github.com/alphabees-25/ilias-plugin-alphabees/releases/latest';
  readonly pluginGuideUrl =
    'https://github.com/alphabees-25/ilias-plugin-alphabees#einbauen';
  readonly pluginChangelogUrl =
    'https://github.com/alphabees-25/ilias-plugin-alphabees/blob/main/CHANGELOG.md';

  /** Der Plugin-Kasten ist zu, solange niemand ihn braucht.
   *
   *  Aufgeklappt sind es zwoelf Zeilen Erklaerung — richtig fuer den, der
   *  gerade verbindet, im Weg fuer alle anderen. Eine gekoppelte Installation
   *  zeigt ihren Zustand auch eingeklappt. */
  pluginOpen = false;

  /** Der erzeugte Plugin-Code. Er kommt genau einmal zurück — danach liegt
   *  nur noch sein Hash in der Datenbank. Deshalb steht er hier, bis der
   *  Dialog schliesst, statt nach dem Kopieren zu verschwinden. */
  pluginCode: string | null = null;
  pluginCodeExpires: string | null = null;
  generatingCode = false;
  codeCopied = false;

  readonly guideStepNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  /** In welcher Sprache die **ILIAS-Oberfläche** des Kunden läuft — nicht die
   *  des Portals. Die Anleitung zitiert Knopf- und Reiternamen wörtlich, und
   *  die heissen in einer englischen ILIAS anders („Default Permissions“ statt
   *  „Rechtevorgaben“). Wer in einer englischen Instanz nach deutschen Namen
   *  sucht, findet sie nie. Voreinstellung ist die Portalsprache, umschalten
   *  kostet einen Klick. */
  iliasLang: 'de' | 'en' = 'de';

  /** Die Begriffe der gewählten ILIAS-Sprache, als Parameter für die
   *  Schritttexte (`{{defaults}}`, `{{read}}` …). */
  get iliasTerms(): Record<string, string> {
    return this.t.instant(`dialogs.iliasConnect.iliasTerms.${this.iliasLang}`) || {};
  }

  setIliasLang(lang: 'de' | 'en'): void {
    this.iliasLang = lang;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private ilias: IliasOverviewService,
    private lms: LmsRegistryService,
    private toastr: NbToastrService,
    private t: TranslateService,
    protected dialogRef: NbDialogRef<IliasConnectDialogComponent>,
  ) {}

  ngOnInit(): void {
    // Die meisten Instanzen laufen in der Sprache, in der auch das Portal
    // bedient wird — als Startwert die bessere Wette als eine feste.
    this.iliasLang = (this.t.currentLang || '').startsWith('en') ? 'en' : 'de';
    this.ilias.listConnections().pipe(takeUntil(this.destroy$)).subscribe({
      next: (rows) => {
        this.connections = rows;
        this.loading = false;
        this.pickInitialPath();
        rows.forEach((row) => this.loadSoap(row.id));
      },
      error: () => {
        this.connections = [];
        this.loading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.dialogRef.close(true);
  }

  toggleGuide(): void {
    this.guideOpen = !this.guideOpen;
  }

  togglePlugin(): void {
    this.pluginOpen = !this.pluginOpen;
  }

  get hasConnections(): boolean {
    return this.connections.length > 0;
  }

  // ---------------------------------------------------------------------
  // Plugin-Weg
  // ---------------------------------------------------------------------

  /** Mindestens eine Verbindung ist gekoppelt.
   *
   *  Steuert, ob der Dialog den Plugin-Zustand zeigt oder die zehn Schritte
   *  der SOAP-Einrichtung. Beides gleichzeitig anzubieten hiesse, einen
   *  Kunden Arbeit machen zu lassen, die sein Plugin bereits erledigt. */
  get hasPairedPlugin(): boolean {
    return this.connections.some((c) => c.plugin?.paired);
  }

  plugin(connection: IliasConnection): IliasPluginState | null {
    return connection.plugin?.paired ? connection.plugin : null;
  }

  /** Erzeugt einen Einmal-Code für die Plugin-Kopplung.
   *
   *  Bewusst derselbe Mechanismus wie bei der dynamischen LTI-Registrierung
   *  (`lti_registration_tokens`): einmalig, 24 Stunden, widerrufbar, nur als
   *  Hash gespeichert. Unterschieden werden die beiden über `purpose` — ein
   *  Plugin-Code wird an der LTI-Registrierung abgewiesen und umgekehrt. */
  generatePluginCode(): void {
    if (this.generatingCode) return;
    this.generatingCode = true;
    this.codeCopied = false;
    this.lms.createLtiRegistrationToken({
      purpose: 'ilias_plugin',
      lms_hint: 'ilias',
      label: this.t.instant('dialogs.iliasConnect.plugin.codeLabel'),
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (token) => {
        this.generatingCode = false;
        this.pluginCode = token.code;
        this.pluginCodeExpires = token.expires_at || null;
      },
      error: (err) => {
        this.generatingCode = false;
        this.toastr.danger(
          err?.error?.detail || this.t.instant('dialogs.iliasConnect.actionFailed'), '',
        );
      },
    });
  }

  copyPluginCode(): void {
    if (!this.pluginCode) return;
    navigator.clipboard.writeText(this.pluginCode).then(
      () => {
        this.codeCopied = true;
        this.toastr.success(this.t.instant('dialogs.iliasConnect.plugin.copied'), '');
      },
      () => {
        // Ohne Zwischenablage-Recht bleibt der Code sichtbar und lässt sich
        // von Hand markieren — kein Grund für eine Fehlermeldung.
        this.codeCopied = false;
      },
    );
  }

  // ---------------------------------------------------------------------
  // Zustand je Verbindung
  // ---------------------------------------------------------------------

  settings(connection: IliasConnection): IliasSyncSettings {
    return connection.auto_sync;
  }

  /** LTI-Seite: die Registrierung trägt Zugangsmaterial und ist nicht getrennt.
   *  Das allein heisst nur, dass Launches ankommen — Daten fliessen deswegen
   *  noch keine. */
  /** Hat diese Verbindung einen LTI-Konsumenten?
   *
   *  Das sagt jetzt das Backend (`lti_linked`), statt es aus dem
   *  Zustandswort zu schliessen: „connected" steht seit dem Plugin-Kanal
   *  fuer beide Wege, und ein Rueckschluss darauf machte aus einer reinen
   *  Plugin-Kopplung eine vermeintliche LTI-Verbindung. Der Rueckfall auf
   *  das alte Verfahren bleibt, solange aeltere Antworten das Feld noch
   *  nicht mitschicken. */
  isLtiLinked(connection: IliasConnection): boolean {
    if (connection.lti_linked !== undefined) return !!connection.lti_linked;
    return connection.connection_status === 'connected'
      || connection.connection_status === 'ready';
  }

  // ---------------------------------------------------------------------
  // Welcher Kanal traegt diese Verbindung?
  // ---------------------------------------------------------------------
  // Es gibt zwei, und sie haben nichts miteinander zu tun:
  //
  //   Plugin       — laeuft IN ILIAS, schiebt Daten selbst, rendert selbst.
  //                  Braucht weder LTI-Zugangsdaten noch SOAP.
  //   LTI + SOAP   — der Launch kommt aus einem Objekt im Kurs, die Daten
  //                  holt unser Server ueber SOAP.
  //
  // Die Karte hat das bisher nicht unterschieden: `isSyncActive` verlangte
  // SOAP, `isConnected` zusaetzlich LTI. Eine reine Plugin-Verbindung konnte
  // damit nie „verbunden" sein, obwohl sie arbeitet — und umgekehrt stand
  // „Getrennt" neben einem gruenen „geprueft", weil das eine die
  // LTI-Verbindung meint und das andere den SOAP-Zugang.

  /** Das Plugin ist gekoppelt und liefert Daten. */
  pluginActive(connection: IliasConnection): boolean {
    const p = this.plugin(connection);
    return !!p && (p.capabilities || []).includes('objects');
  }

  /** Deckt das Plugin ab, wofuer sonst SOAP noetig waere? */
  pluginCoversData(connection: IliasConnection): boolean {
    const caps = this.plugin(connection)?.capabilities || [];
    return caps.includes('objects') && caps.includes('members');
  }

  /** Gehoert SOAP zu dieser Verbindung?
   *
   *  Nein, sobald das Plugin die Daten liefert — dann ist der technische
   *  Zugang toter Ballast und seine Chips verwirren nur. Hinterlegte
   *  Zugangsdaten bleiben trotzdem sichtbar, sonst waeren sie ohne Weg zum
   *  Loeschen eingesperrt. */
  usesSoap(connection: IliasConnection): boolean {
    return !this.pluginCoversData(connection) || this.soapConfigured(connection);
  }

  /** 'plugin' | 'lti' | 'both' | 'none' */
  channelOf(connection: IliasConnection): string {
    const plugin = this.pluginActive(connection);
    const lti = this.isLtiLinked(connection);
    if (plugin && lti) return 'both';
    if (plugin) return 'plugin';
    if (lti || this.soapConfigured(connection)) return 'lti';
    return 'none';
  }

  channelChipKey(connection: IliasConnection): string {
    return 'dialogs.iliasConnect.channel.' + this.channelOf(connection);
  }

  /** Abgleich läuft — je nach Kanal auf verschiedenen Wegen.
   *
   *  Plugin: es schiebt von selbst, sobald es gekoppelt ist.
   *  SOAP:   Zugangsdaten hinterlegt, eingeschaltet, zuletzt ohne Fehler. */
  isSyncActive(connection: IliasConnection): boolean {
    if (this.pluginCoversData(connection)) return true;
    return this.soapConfigured(connection)
      && !!this.settings(connection)?.enabled
      && !this.lastError(connection);
  }

  /** „Verbunden" heisst: dieser Kanal arbeitet gerade.
   *
   *  Fuer das Plugin reicht die Kopplung — es rendert und schiebt dann von
   *  selbst. Fuer LTI verlangt es beides, Verknuepfung UND laufenden
   *  Abgleich: gruen allein fuer eine Registrierung waere ein Versprechen,
   *  das die Kachel nicht haelt, es ist dann noch nichts abgeglichen. */
  isConnected(connection: IliasConnection): boolean {
    if (connection.paused || connection.connection_status === 'disconnected') {
      return false;
    }
    if (this.pluginActive(connection)) return true;
    return this.isLtiLinked(connection) && this.isSyncActive(connection);
  }

  /** ILIAS vor Version 11 kann keine spezifikationskonforme dynamische
   *  Registrierung. Unbekannte Versionen gelten nicht als veraltet — ein
   *  falscher Hinweis wäre schlimmer als keiner. */
  isOutdated(connection: IliasConnection): boolean {
    return connection.platform_major !== null && connection.platform_major < 11;
  }

  connectionChipClass(connection: IliasConnection): string {
    if (connection.connection_status === 'disconnected') return 'is-danger';
    if (connection.paused) return 'is-warning';
    return this.isConnected(connection) ? 'is-connected' : '';
  }

  connectionChipKey(connection: IliasConnection): string {
    if (connection.connection_status === 'disconnected') {
      return 'dialogs.iliasConnect.stateDisconnected';
    }
    if (connection.paused) return 'dialogs.iliasConnect.statePaused';
    if (this.isConnected(connection)) return 'dialogs.iliasConnect.stateConnected';
    return this.isLtiLinked(connection)
      ? 'dialogs.iliasConnect.stateLtiOnly'
      : 'dialogs.iliasConnect.statePending';
  }

  /** Beschriftung der Pause-Zeile. Drei Zustaende: getrennt, pausiert,
   *  laufend. Getrennt und pausiert sehen in der Datenbank gleich aus —
   *  beide setzen `disabled_at` —, sind aber verschiedene Dinge: aus einer
   *  Pause kommt man durch Fortsetzen zurueck, aus dem Trennen nur durch
   *  neues Verbinden. */
  pauseRowLabelKey(connection: IliasConnection): string {
    if (connection.connection_status === 'disconnected') {
      return 'dialogs.iliasConnect.disconnectedLabel';
    }
    return connection.paused
      ? 'dialogs.iliasConnect.resume'
      : 'dialogs.iliasConnect.pause';
  }

  pauseRowTooltipKey(connection: IliasConnection): string {
    if (connection.connection_status === 'disconnected') {
      return 'dialogs.iliasConnect.disconnectedTooltip';
    }
    return connection.paused
      ? 'dialogs.iliasConnect.resumeTooltip'
      : 'dialogs.iliasConnect.pauseTooltip';
  }

  togglePause(connection: IliasConnection): void {
    if (this.busy[connection.id]) return;
    this.busy[connection.id] = true;
    const call = connection.paused
      ? this.lms.resumeRegistration(connection.id)
      : this.lms.pauseRegistration(connection.id);
    call.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.busy[connection.id] = false;
        this.reload();
      },
      error: (err) => {
        this.busy[connection.id] = false;
        this.toastr.danger(
          err?.error?.detail || this.t.instant('dialogs.iliasConnect.actionFailed'), '',
        );
      },
    });
  }

  disconnect(connection: IliasConnection): void {
    if (this.busy[connection.id]) return;
    this.busy[connection.id] = true;
    this.lms.disconnectRegistration(connection.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busy[connection.id] = false;
          this.toastr.success(this.t.instant('dialogs.iliasConnect.disconnected'), '');
          this.reload();
        },
        error: (err) => {
          this.busy[connection.id] = false;
          this.toastr.danger(
            err?.error?.detail || this.t.instant('dialogs.iliasConnect.actionFailed'), '',
          );
        },
      });
  }

  onPiiToggle(connection: IliasConnection, enabled: boolean): void {
    this.saveSettings(connection, { pii_enabled: enabled });
  }

  private reload(): void {
    this.ilias.listConnections().pipe(takeUntil(this.destroy$)).subscribe({
      next: (rows) => {
        this.connections = rows;
      },
      error: () => undefined,
    });
  }

  soapConfigured(connection: IliasConnection): boolean {
    return !!this.soap[connection.id]?.configured || connection.soap_configured;
  }

  /** Drei Marken wie bei Moodle: Umgebung, Zugang, Abgleich. */
  soapChipClass(connection: IliasConnection): string {
    // Eine getrennte Verbindung ist getrennt — auch wenn die
    // SOAP-Zugangsdaten noch stimmen. Ein gruenes „geprueft" neben einem
    // roten „Getrennt" las sich wie ein Widerspruch, und beides war fuer
    // sich richtig: das eine meint den Zugang, das andere die Verbindung.
    if (connection.connection_status === 'disconnected' || connection.paused) {
      return 'is-muted';
    }
    const cfg = this.soap[connection.id];
    if (!cfg?.configured) return 'is-warning';
    if (cfg.last_error) return 'is-danger';
    return cfg.last_ok_at ? 'is-connected' : 'is-warning';
  }

  soapChipKey(connection: IliasConnection): string {
    const cfg = this.soap[connection.id];
    if (!cfg?.configured) return 'dialogs.iliasConnect.soap.status.incomplete';
    if (cfg.last_error) return 'dialogs.iliasConnect.soap.status.error';
    return cfg.last_ok_at
      ? 'dialogs.iliasConnect.soap.status.ok'
      : 'dialogs.iliasConnect.soap.status.incomplete';
  }

  lastError(connection: IliasConnection): string | null {
    return this.soap[connection.id]?.last_error || connection.auto_sync?.last_error || null;
  }

  // ---------------------------------------------------------------------
  // Zugang
  // ---------------------------------------------------------------------

  private loadSoap(registrationId: string): void {
    this.lms.getIliasSoap(registrationId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (cfg) => {
        this.soap[registrationId] = cfg;
        this.forms[registrationId] = {
          // Der Pfad wechselte mit ILIAS 10; der Vorschlag kommt aus der
          // Issuer-URL und erspart das Nachschlagen.
          soap_url: cfg.soap_url || cfg.suggested_soap_url || '',
          ilias_client: cfg.ilias_client || '',
          username: cfg.username || '',
          password: '',
          template_ref_id: cfg.template_ref_id ?? null,
        };
      },
      error: () => undefined,
    });
  }

  saveSoap(connection: IliasConnection): void {
    const form = this.forms[connection.id];
    if (!form || this.busy[connection.id]) return;
    this.busy[connection.id] = true;

    const payload: IliasSoapInput = {
      soap_url: form.soap_url.trim(),
      ilias_client: form.ilias_client.trim(),
      username: form.username.trim(),
      template_ref_id: form.template_ref_id ?? null,
    };
    // Leeres Feld heisst „behalten", nicht „löschen" — das Passwort verlässt
    // den Server nie und kann beim Ändern anderer Felder nicht mitgesendet
    // werden.
    if (form.password) payload.password = form.password;

    this.lms.saveIliasSoap(connection.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (cfg) => {
          this.soap[connection.id] = cfg;
          form.password = '';
          this.busy[connection.id] = false;
          this.toastr.success(this.t.instant('dialogs.iliasConnect.soap.saved'), '');
        },
        error: (err) => {
          this.busy[connection.id] = false;
          this.toastr.danger(
            err?.error?.detail || this.t.instant('dialogs.iliasConnect.soap.saveFailed'), '',
          );
        },
      });
  }

  testSoap(connection: IliasConnection): void {
    if (this.testing[connection.id]) return;
    this.testing[connection.id] = true;
    this.lms.testIliasSoap(connection.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.testing[connection.id] = false;
        if (result.ok && result.content_visible === false) {
          // Grün wäre hier gelogen: die Anmeldung stimmt, aber der technische
          // User sieht kein einziges Magazin-Objekt — ihm fehlt das Leserecht
          // auf Kurse und Kategorien, und der Abgleich fände nichts.
          this.toastr.warning(
            this.t.instant('dialogs.iliasConnect.soap.testNoContent'), '',
            { duration: 0 },
          );
        } else if (result.ok) {
          this.toastr.success(
            this.t.instant('dialogs.iliasConnect.soap.testOk', {
              count: result.root_children ?? 0,
            }), '',
          );
        } else {
          this.toastr.danger(
            `${this.t.instant('dialogs.iliasConnect.soap.step.' + result.step)}: ${result.detail || ''}`,
            '',
          );
        }
        this.loadSoap(connection.id);
      },
      error: (err) => {
        this.testing[connection.id] = false;
        this.toastr.danger(
          err?.error?.detail || this.t.instant('dialogs.iliasConnect.soap.testFailed'), '',
        );
      },
    });
  }

  // ---------------------------------------------------------------------
  // Datenabgleich
  // ---------------------------------------------------------------------

  onAutoSyncToggle(connection: IliasConnection, enabled: boolean): void {
    this.saveSettings(connection, { enabled });
  }

  onModeChange(connection: IliasConnection, mode: 'placements' | 'all'): void {
    this.saveSettings(connection, { mode });
  }

  onRootRefIdChange(connection: IliasConnection, value: string): void {
    const parsed = Number.parseInt((value || '').trim(), 10);
    this.saveSettings(connection, { root_ref_id: Number.isFinite(parsed) ? parsed : 0 });
  }

  private saveSettings(
    connection: IliasConnection,
    patch: Partial<Pick<IliasSyncSettings, 'enabled' | 'mode' | 'root_ref_id' | 'pii_enabled'>>,
  ): void {
    this.busy[connection.id] = true;
    this.ilias.saveSyncSettings(connection.id, patch)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          connection.auto_sync = settings;
          this.busy[connection.id] = false;
          // Die Zeilen speichern beim Umlegen, ohne Knopf. Ohne Quittung
          // bleibt offen, ob die Wahl angekommen ist — besonders beim
          // Umfang, der über den ganzen Abgleich entscheidet.
          this.toastr.success(this.t.instant('dialogs.iliasConnect.sync.saved'), '');
        },
        error: (err) => {
          this.busy[connection.id] = false;
          this.toastr.danger(
            err?.error?.detail || this.t.instant('dialogs.iliasConnect.sync.saveFailed'), '',
          );
        },
      });
  }

  runSync(connection: IliasConnection): void {
    if (this.syncing[connection.id]) return;
    this.syncing[connection.id] = true;
    this.ilias.runSync(connection.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        this.syncing[connection.id] = false;
        const courses = result.tree?.courses ?? 0;
        if (courses === 0) {
          // Derselbe stille Fall wie beim Verbindungstest: ILIAS meldet ein
          // fehlendes Leserecht nicht als Fehler, sondern liefert eine leere
          // Antwort. Ein grüner „0 Kurse abgeglichen“ wäre die falsche
          // Auskunft — der Hinweis nennt das fehlende Recht.
          this.toastr.warning(
            this.t.instant('dialogs.iliasConnect.soap.testNoContent'), '',
            { duration: 0 },
          );
        } else {
          this.toastr.success(
            this.t.instant('dialogs.iliasConnect.sync.done', {
              courses,
              users: result.members?.users ?? 0,
            }), '',
          );
        }
        this.loadSoap(connection.id);
      },
      error: (err) => {
        this.syncing[connection.id] = false;
        this.toastr.danger(
          err?.error?.detail || this.t.instant('dialogs.iliasConnect.sync.failed'), '',
        );
      },
    });
  }

  // ---------------------------------------------------------------------
  // Kursinhalte
  // ---------------------------------------------------------------------

  /** Drei Zustände in einem Feld. `off` heisst nicht „nie", sondern „nur was
   *  je Platzierung gesetzt ist" — genau wie bei Moodle. */
  contentScope(connection: IliasConnection): IliasContentScope {
    const settings = connection.auto_sync;
    if (!settings?.content_enabled) return 'off';
    return settings.content_mode === 'all' ? 'all' : 'placements';
  }

  onContentScopeChange(connection: IliasConnection, scope: IliasContentScope): void {
    this.saveContent(connection, scope === 'off'
      ? { enabled: false }
      : { enabled: true, mode: scope });
  }

  onStructureParseChange(connection: IliasConnection, enabled: boolean): void {
    this.saveContent(connection, { structure_parse: enabled });
  }

  private saveContent(
    connection: IliasConnection,
    patch: { enabled?: boolean; mode?: 'placements' | 'all'; structure_parse?: boolean },
  ): void {
    this.busy[connection.id] = true;
    this.ilias.saveContentSync(connection.id, patch)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          connection.auto_sync = settings;
          this.busy[connection.id] = false;
        },
        error: (err) => {
          this.busy[connection.id] = false;
          this.toastr.danger(
            err?.error?.detail || this.t.instant('dialogs.iliasConnect.sync.saveFailed'), '',
          );
        },
      });
  }
}
